import { useRouter } from 'next/router';
import React from 'react';

import Button from 'components/Button/Button';
import { useCopyStore, useGlobalStore } from 'store';
import { ThemeChapterProps } from 'store/copy.types';
import gtm, { actions, categories } from 'u9/utils/gtm';
import { ROUTES } from 'utils/routes';

import * as Styled from './Navigation.styles';

export interface NavigationProps {
  items: ThemeChapterProps;
  backButtonText: string;
  accessibilitBackButtonText?: string;
  handleBack: () => void;
}

const defaultProps: Partial<NavigationProps> = {
  items: {},
  backButtonText: null,
  accessibilitBackButtonText: null,
};

const Navigation: React.FC<NavigationProps> = ({
  items,
  backButtonText,
  accessibilitBackButtonText,
  handleBack,
}) => {
  const router = useRouter();
  const {
    currentChapter,
    setCurrentChapter,
    isLandingVisible,
    isSidebarExpanded,
  } = useGlobalStore();
  const {
    copy: { global },
  } = useCopyStore();

  const handleChapterClick = (value: any, chapter: string) => {
    setCurrentChapter(chapter);
    gtm.trackEvent(
      categories[`${router.query.id}`],
      actions.click,
      value?.menuBtnText || 'chapter'
    );
  };

  return (
    <Styled.Container
      aria-hidden={
        router.pathname === ROUTES.HOME || isLandingVisible || isSidebarExpanded
      }
      role="navigation"
    >
      <Styled.SkipToContent
        href={`#${global?.common?.skipToContentId}`}
        aria-label={global?.common?.skipToContent}
        aria-hidden={isSidebarExpanded}
        tabIndex={isSidebarExpanded ? -1 : 0}
      >
        {global?.common?.skipToContent}
      </Styled.SkipToContent>
      <Styled.Wrapper>
        <Styled.BackButtonWrapper>
          <Button
            icon="arrowLeft"
            text={backButtonText}
            type="black"
            onClick={handleBack}
            ariaHidden={isSidebarExpanded}
            aria-label={
              accessibilitBackButtonText
                ? accessibilitBackButtonText
                : backButtonText
            }
            tabIndex={isSidebarExpanded ? -1 : 0}
          />
        </Styled.BackButtonWrapper>
        <Styled.Group>
          {Object.entries(items).map(([key, value], index: number) => (
            <Styled.ButtonWrapper
              key={key}
              initial={{ opacity: 0, x: 50 }}
              animate={items ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.6, delay: (index + 1) * 0.3 }}
            >
              <Button
                icon={value?.menuIcon ? value?.menuIcon : ''}
                text={value?.menuBtnText}
                onClick={() => handleChapterClick(value, key)}
                isActive={currentChapter === key}
                ariaCurrent={currentChapter === key}
              />
            </Styled.ButtonWrapper>
          ))}
        </Styled.Group>
      </Styled.Wrapper>
    </Styled.Container>
  );
};

Navigation.defaultProps = defaultProps;

export default Navigation;
